// client/src/components/company/VatCreditRequestDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatInterestRate, formatCurrencyInput, parseCurrencyInput, formatCurrencyWithSymbol, formatSubmittedAt } from '../utils/formatUtils';
import { Info } from 'lucide-react';
import VatCreditAttachmentList from '../vatCredit/VatCreditAttachmentList';
import VatCreditCostBreakdown from '../vatCredit/VatCreditCostBreakdown';
import {
  formatCreditTypeAndPeriod,
  formatMaturity,
  formatCoverage,
  formatRequestedRateTypes,
  formatDayCountBasis,
  formatOptionalDate,
  computeBidCost
} from '../vatCredit/vatCreditUtils';
import '../../styles/RequestDetail.css';
import '../../styles/VatCreditRequestDetail.css';

const VatCreditRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Dashboard the user came from (set by the dashboard links), otherwise back to the requests list
  const backToDashboard = useLocation().state?.from;
  const { t, i18n } = useTranslation();

  const [request, setRequest] = useState(null);
  const [bids, setBids] = useState([]);
  const [affiliatedBanks, setAffiliatedBanks] = useState([]);
  const [thirdPartyBanks, setThirdPartyBanks] = useState([]);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const [allocations, setAllocations] = useState({});
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [allocationError, setAllocationError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingAllocation, setProcessingAllocation] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [allocationCompleted, setAllocationCompleted] = useState(false);

  // Track if we're currently editing an allocation input
  const [editingInputId, setEditingInputId] = useState(null);
  const [editingInputValue, setEditingInputValue] = useState('');

  // Helper function to get the correct bid ID
  const getBidId = (bid) => {
    return bid?._id || bid?.id;
  };

  // Helper function to check if there are any active bids
  const hasActiveBids = (bids) => {
    return bids.some(bid => bid.status === 'pending');
  };

  // Risolve i nomi delle banche selezionate, come nelle richieste di allocazione (RequestDetail.js)
  const getSelectedBankNames = () => {
    if (!request || !request.selectedBanks || request.selectedBanks.length === 0 || !banksLoaded) {
      return [];
    }

    const bankNames = request.selectedBanks.map(selectedBankId => {
      const affiliatedBank = affiliatedBanks.find(ab =>
        ab.userId?.toString() === selectedBankId?.toString() ||
        ab._id?.toString() === selectedBankId?.toString()
      );

      if (affiliatedBank) {
        if (affiliatedBank.organizationId && affiliatedBank.organizationId.name) {
          return affiliatedBank.organizationId.name;
        }
        if (affiliatedBank.referralDetails && affiliatedBank.referralDetails.bankName) {
          return affiliatedBank.referralDetails.bankName;
        }
        if (affiliatedBank.bankName) {
          return affiliatedBank.bankName;
        }
        if (affiliatedBank.firstName && affiliatedBank.lastName) {
          return `${affiliatedBank.firstName} ${affiliatedBank.lastName}`;
        }
      }

      const thirdPartyBank = thirdPartyBanks.find(tp =>
        tp._id?.toString() === selectedBankId?.toString()
      );

      if (thirdPartyBank) {
        if (thirdPartyBank.organization?.name) {
          return thirdPartyBank.organization.name;
        }
        if (thirdPartyBank.firstName && thirdPartyBank.lastName) {
          return `${thirdPartyBank.firstName} ${thirdPartyBank.lastName}`;
        }
      }

      return null;
    });

    return [...new Set(bankNames.filter(Boolean))];
  };

  // Helper function to get localized server error messages
  const getLocalizedServerError = (serverMessage) => {
    if (typeof serverMessage !== 'string') return serverMessage;

    const lowerMessage = serverMessage.toLowerCase();

    if (i18n.language === 'it') {
      if (lowerMessage.includes('total allocation exceeds')) {
        return 'L\'allocazione totale supera l\'importo della richiesta';
      }
      if (lowerMessage.includes('exceeds the bid')) {
        return 'L\'allocazione supera l\'importo massimo dell\'offerta';
      }
      if (lowerMessage.includes('not in the pending allocation state')) {
        return 'La richiesta non è in stato di allocazione in attesa';
      }
      if (lowerMessage.includes('not found')) {
        return 'Risorsa non trovata';
      }
    }

    return serverMessage;
  };

  useEffect(() => {
    const fetchRequestDetails = async () => {
      try {
        const res = await axios.get(`/api/vat-credit-requests/${id}`);

        setRequest(res.data.request);

        // Ordine crescente per tasso: il tasso più basso è il migliore per l'azienda
        const sortedBids = [...(res.data.bids || [])].sort((a, b) => a.interestRate - b.interestRate);
        setBids(sortedBids);

        // Initialize allocations for pending allocation requests
        if (res.data.request.status === 'pending_allocation') {
          const initialAllocations = {};

          sortedBids.forEach(bid => {
            const bidId = getBidId(bid);

            if (!bidId) {
              console.error('Invalid bid object (no ID found)');
              return;
            }

            initialAllocations[bidId] = {
              amount: 0
            };
          });

          setAllocations(initialAllocations);
          setTotalAllocated(0);
        }

        setLoading(false);
      } catch (err) {
        setError(t('common.errors.fetchDetails'));
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [id, t]);

  // Fetch delle banche (relazione + terze) per risolvere selectedBanks in nomi, come RequestDetail.js
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const referralsRes = await axios.get('/api/company/bank-referrals');
        setAffiliatedBanks(referralsRes.data.bankReferrals || []);

        const thirdPartyRes = await axios.get('/api/requests/third-party-banks');
        setThirdPartyBanks(thirdPartyRes.data.thirdPartyBanks || []);
      } catch (err) {
        setAffiliatedBanks([]);
        setThirdPartyBanks([]);
      } finally {
        setBanksLoaded(true);
      }
    };

    fetchBanks();
  }, []);

  const handleAllocationChange = (bidId, value) => {
    if (!allocations[bidId]) {
      console.error('Tried to update allocation for non-existent bid');
      return;
    }

    // Keep the formatted string for display during editing
    const rawValue = value;
    let formatted;
    if (rawValue.endsWith('.') || rawValue.endsWith(',')) {
      formatted = formatCurrencyInput(rawValue.slice(0, -1));
      formatted = formatted ? formatted + '.' : '';
    } else {
      formatted = formatCurrencyInput(rawValue);
    }
    setEditingInputValue(formatted);

    // Parse the numeric value for calculations
    const amount = parseCurrencyInput(value);

    const updatedAllocations = {
      ...allocations,
      [bidId]: {
        ...allocations[bidId],
        amount
      }
    };

    setAllocations(updatedAllocations);

    const total = Object.values(updatedAllocations).reduce(
      (sum, alloc) => sum + alloc.amount,
      0
    );

    setTotalAllocated(total);

    if (total > request.amount) {
      setAllocationError(t('vatCredit.errors.exceedsRequestAmount'));
    } else {
      setAllocationError('');
    }
  };

  const handleInputFocus = (bidId) => {
    setEditingInputId(bidId);
    if (allocations[bidId]) {
      setEditingInputValue(formatCurrencyInput(allocations[bidId].amount));
    }
  };

  const handleInputBlur = () => {
    setEditingInputId(null);
  };

  // Helper to prepare bid allocations array
  const prepareBidAllocations = () => {
    const bidAllocations = [];

    for (const bid of bids) {
      if (!bid) continue;

      const bidId = getBidId(bid);
      if (!bidId) continue;

      const allocation = allocations[bidId];
      if (!allocation) continue;

      if (bid.status !== 'pending') continue;

      if (allocation.amount > 0) {
        bidAllocations.push({
          bidId: bidId,
          amount: allocation.amount
        });
      }
    }

    return bidAllocations;
  };

  const confirmAllocation = async () => {
    if (totalAllocated > request.amount) {
      return setAllocationError(t('vatCredit.errors.exceedsRequestAmount'));
    }

    const hasBidsWithAllocation = Object.values(allocations).some(allocation =>
      allocation.amount > 0
    );

    if (!hasBidsWithAllocation) {
      return setAllocationError(t('allocation.errors.noAllocation'));
    }

    for (const bid of bids) {
      if (!bid || bid.status !== 'pending') continue;

      const bidId = getBidId(bid);
      if (!bidId || !allocations[bidId]) continue;

      const allocation = allocations[bidId];
      if (allocation.amount > bid.desiredAmount) {
        return setAllocationError(
          i18n.language === 'it'
            ? `L'allocazione per ${bid.bank?.name} (${formatCurrencyWithSymbol(allocation.amount, request.currency || 'EUR')}) supera l'importo massimo (${formatCurrencyWithSymbol(bid.desiredAmount, request.currency || 'EUR')})`
            : `Allocation for ${bid.bank?.name} (${formatCurrencyWithSymbol(allocation.amount, request.currency || 'EUR')}) exceeds their maximum amount (${formatCurrencyWithSymbol(bid.desiredAmount, request.currency || 'EUR')})`
        );
      }
    }

    setAllocationError('');

    await executeAllocation();
  };

  const executeAllocation = async () => {
    try {
      setProcessingAllocation(true);
      setError('');
      setAllocationError('');

      const bidAllocations = prepareBidAllocations();

      if (bidAllocations.length === 0) {
        setProcessingAllocation(false);
        return setAllocationError(t('allocation.errors.noActiveBids'));
      }

      await axios.post('/api/vat-credit-bids/allocate', {
        requestId: request.id || request._id,
        allocations: bidAllocations
      });

      setAllocationCompleted(true);

      setTimeout(() => {
        navigate('/company/vat-credit/past-requests', { replace: true });
      }, 2500);

    } catch (err) {
      setProcessingAllocation(false);

      if (err.response?.status === 400 && err.response?.data?.message) {
        setAllocationError(getLocalizedServerError(err.response.data.message));
      } else if (err.response?.status === 404) {
        setError(i18n.language === 'it' ? 'Risorsa non trovata' : 'Resource not found');
      } else {
        setError(i18n.language === 'it'
          ? 'Impossibile confermare l\'allocazione. Contatta l\'amministratore.'
          : 'Unable to confirm the allocation. Contact the administrator.');
      }
    }
  };

  const updateRequestStatus = async (newStatus) => {
    if (newStatus === 'cancelled') {
      const confirmMessage = i18n.language === 'it'
        ? 'Sei sicuro di voler annullare questa richiesta? Questa azione non può essere annullata.'
        : 'Are you sure you want to cancel this request? This action cannot be undone.';

      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      setProcessingAction(true);
      setError('');

      await axios.put(`/api/vat-credit-requests/${id}/status`, { status: newStatus });

      if (newStatus === 'cancelled') {
        navigate('/company/vat-credit/past-requests');
      } else {
        const res = await axios.get(`/api/vat-credit-requests/${id}`);
        setRequest(res.data.request);

        const sortedBids = [...(res.data.bids || [])].sort((a, b) => a.interestRate - b.interestRate);
        setBids(sortedBids);

        if (newStatus === 'pending_allocation') {
          const initialAllocations = {};

          sortedBids.forEach(bid => {
            const bidId = getBidId(bid);

            if (!bidId) {
              console.error('Invalid bid object (no ID found)');
              return;
            }

            initialAllocations[bidId] = {
              amount: 0
            };
          });

          setAllocations(initialAllocations);
          setTotalAllocated(0);
        }
      }

      setProcessingAction(false);
    } catch (err) {
      console.error('Error updating VAT credit request status:');

      let errorMessage = t('common.errors.updateStatus');
      if (err.response?.data?.message) {
        errorMessage = getLocalizedServerError(err.response.data.message);
      }

      setError(errorMessage);
      setProcessingAction(false);
    }
  };

  // Render bids come mobile cards
  const renderMobileBidCard = (bid) => {
    const bidId = getBidId(bid);
    if (!bid || !bidId) return null;

    const isCancelled = bid.status === 'cancelled';

    return (
      <div key={bidId} className={`mobile-record-card ${isCancelled ? 'cancelled' : ''}`}>
        <div className="mobile-card-header">
          <div className="mobile-card-title">
            {bid.bank?.name || t('bids.notAvailable')}
          </div>
          <div className="mobile-card-status">
            <span className={`status status-${bid.status}`}>
              {t(`status.${bid.status}`)}
            </span>
          </div>
        </div>

        <div className="mobile-card-body">
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.desiredAmount')}</span>
            <span className="mobile-card-value">
              {formatCurrencyWithSymbol(bid.desiredAmount, request.currency || 'EUR')}
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.cost.rate')}</span>
            <span className="mobile-card-value">
              <span className="interest-rate">
                {formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}
              </span>
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.commission')}</span>
            <span className="mobile-card-value">
              {bid.commission !== undefined && bid.commission !== null
                ? `${bid.commission}%`
                : t('bids.notAvailable')}
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.fees')}</span>
            <span className="mobile-card-value">
              {bid.fees !== undefined && bid.fees !== null
                ? formatCurrencyWithSymbol(bid.fees, request.currency || 'EUR')
                : t('bids.notAvailable')}
            </span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.officialMaturityDate')}</span>
            <span className="mobile-card-value">{formatOptionalDate(bid.officialMaturityDate, t)}</span>
          </div>

          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('vatCredit.dayCountBasis')}</span>
            <span className="mobile-card-value">{formatDayCountBasis(bid, t) || t('bids.notAvailable')}</span>
          </div>

          {request.status === 'pending_allocation' && allocations[bidId] && (
            <div className="mobile-card-row" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <span className="mobile-card-label" style={{ marginBottom: '8px' }}>
                {t('allocation.yourAllocation')}
              </span>
              <input
                type="text"
                className="mobile-allocation-input"
                value={editingInputId === bidId
                  ? editingInputValue
                  : formatCurrencyWithSymbol(allocations[bidId].amount, request.currency || 'EUR')}
                onChange={(e) => !isCancelled && handleAllocationChange(bidId, e.target.value)}
                onFocus={() => !isCancelled && handleInputFocus(bidId)}
                onBlur={handleInputBlur}
                disabled={processingAllocation || isCancelled}
                placeholder={t('allocation.enterAmount')}
              />
            </div>
          )}

          {request.status === 'completed' && (
            <>
              <div className="mobile-card-row">
                <span className="mobile-card-label">{t('bids.allocatedAmount')}</span>
                <span className={`mobile-card-value ${bid.status === 'approved' ? 'amount-display allocated' : ''}`}>
                  {bid.status === 'approved' ? formatCurrencyWithSymbol(bid.allocatedAmount, request.currency || 'EUR') : '—'}
                </span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">{t('requests.allocatedBy')}</span>
                <span className="mobile-card-value">
                  {bid.status === 'approved' && bid.allocatedBy ? `${bid.allocatedBy.firstName} ${bid.allocatedBy.lastName}` : '—'}
                </span>
              </div>
            </>
          )}
        </div>

        {bid.notes && (
          <div className="mobile-bid-extra">
            <div className="detail-item-modern">
              <div className="detail-label">{t('requestDetail.bidDetails.bankNotes')}</div>
              <div className="detail-value">{bid.notes}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="loading">{t('common.loadingRequests')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!request) {
    const notFoundMessage = i18n.language === 'it' ? 'Richiesta non trovata' : 'Request not found';
    return <div className="error-message">{notFoundMessage}</div>;
  }

  if (allocationCompleted) {
    return (
      <div className="card">
        <div className="alert-success" style={{ padding: '20px', margin: '20px 0' }}>
          <h3>{t('requestDetail.messages.allocationCompletedSuccess')}</h3>
        </div>
      </div>
    );
  }

  const showAllocationColumn = request.status === 'pending_allocation';

  const isCompleted = request.status === 'completed';

  // Richiesta completata: costi salvati al momento dell'allocazione
  const completedCostRows = bids
    .filter(bid => bid.status === 'approved' && bid.allocatedAmount > 0)
    .map(bid => ({
      key: getBidId(bid),
      bankName: bid.bank?.name || t('bids.notAvailable'),
      amount: bid.allocatedAmount,
      cost: bid.allocationCost
    }));

  // Calcolatore, una riga per ogni offerta non annullata:
  // - in allocazione è dinamico: costo sull'importo allocato (riga senza costi finché è 0)
  // - a richiesta completata mostra solo le offerte allocate, con i costi salvati all'allocazione
  // - altrimenti simula il costo sull'importo massimo dell'offerta
  const calculatorBids = bids.filter(bid => bid.status !== 'cancelled');
  const calculatorRows = isCompleted ? completedCostRows : calculatorBids.map(bid => {
    const bidId = getBidId(bid);
    const row = { key: bidId, bankName: bid.bank?.name || t('bids.notAvailable') };

    if (!showAllocationColumn) {
      return { ...row, amount: bid.desiredAmount, cost: computeBidCost(bid, bid.desiredAmount, request.executionDate) };
    }

    const amount = allocations[bidId]?.amount || 0;
    return amount > 0
      ? { ...row, amount, cost: computeBidCost(bid, amount, request.executionDate) }
      : { ...row, amount: 0, cost: null, notAllocated: true };
  });

  // Data dell'Euribor usato nella stima (in allocazione solo se c'è un'offerta variabile allocata;
  // a richiesta completata i costi sono definitivi, nessuna stima)
  const calculatorBaseRateDate = isCompleted ? undefined : calculatorBids.find(bid =>
    bid.rateType === 'floating' && bid.baseRateDate &&
    (!showAllocationColumn || allocations[getBidId(bid)]?.amount > 0)
  )?.baseRateDate;

  return (
    <div className="request-detail company-dashboard">
      <div className="page-navigation" style={{ marginBottom: '14px' }}>
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate(backToDashboard || '/company/vat-credit/past-requests')}
          disabled={processingAction}
        >
          {backToDashboard ? t('requestDetail.navigation.backToDashboard') : t('requestDetail.navigation.backToRequests')}
        </button>
      </div>

      <div className="card">
        <div className="card-title">
          <span>
            <Info className="card-title-icon" size={20} />
            {t('vatCredit.requestDetails')}
          </span>
          <div>
            <span className={`status status-${request.status}`}>
              {t(`status.${request.status}`)}
            </span>
          </div>
        </div>

        <div className="request-info">
          <h3>
            {t('vatCredit.requestInformation')}
            <span className="request-info-subtitle">
              {t('requestDetail.info.requestMeta', {
                id: request.requestId,
                date: new Date(request.createdAt).toLocaleDateString(),
                name: request.createdBy
                  ? `${request.createdBy.firstName} ${request.createdBy.lastName}`
                  : t('bids.notAvailable')
              })}
            </span>
          </h3>

          <div className="info-grid info-list vc-info-list">
            {/* Righe con due campi affiancati: tipo/periodo e copertura, importo a rimborso e importo da cedere,
                data di erogazione e termine maturity, tasso da quotare e banche selezionate */}
            {request.creditType && (
              <div className="info-list-pair">
                <div className="info-card">
                  <div className="info-card-label">{t('vatCredit.creditPeriod')}</div>
                  <div className="info-card-value">{formatCreditTypeAndPeriod(request, t)}</div>
                </div>

                <div className="info-card">
                  <div className="info-card-label">{t('vatCredit.coverage')}</div>
                  <div className="info-card-value">{formatCoverage(request, t)}</div>
                </div>
              </div>
            )}

            <div className="info-list-pair">
              {request.creditType && (
                <div className="info-card">
                  <div className="info-card-label">{t('vatCredit.refundRequestedAmount')}</div>
                  <div className="info-card-value">{formatCurrencyWithSymbol(request.refundRequestedAmount, request.currency || 'EUR')}</div>
                </div>
              )}

              <div className="info-card amount">
                <div className="info-card-label">{t('vatCredit.amountToAssign')}</div>
                <div className="info-card-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</div>
              </div>
            </div>

            <div className="info-list-pair">
              <div className="info-card">
                <div className="info-card-label">{t('vatCredit.executionDate')}</div>
                <div className="info-card-value">{new Date(request.executionDate).toLocaleDateString()}</div>
              </div>

              <div className="info-card">
                <div className="info-card-label">{t('vatCredit.maturityDate')}</div>
                <div className="info-card-value">{formatMaturity(request, t)}</div>
              </div>
            </div>

            {(request.requestedRateTypes?.length > 0 || request.selectedBanks?.length > 0) && (
              <div className="info-list-pair">
                {request.requestedRateTypes?.length > 0 && (
                  <div className="info-card">
                    <div className="info-card-label">{t('vatCredit.requestedRateType')}</div>
                    <div className="info-card-value">{formatRequestedRateTypes(request, t)}</div>
                  </div>
                )}

                {request.selectedBanks?.length > 0 && (
                  <div className="info-card">
                    <div className="info-card-label">{t('requests.selectedBanks')}</div>
                    <div className="info-card-value">
                      {(() => {
                        const bankNames = getSelectedBankNames();
                        return bankNames.length > 0
                          ? bankNames.join(', ')
                          : `${request.selectedBanks.length} ${t('requests.banksSelected')}`;
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="info-card">
              <div className="info-card-label">{t('vatCredit.bidDeadline')}</div>
              <div className="info-card-value">
                {new Date(request.bidDeadline).toLocaleDateString()}
                {' '}
                <span style={{ color: '#6c757d', fontSize: '0.9em' }}>
                  {new Date(request.bidDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} CET
                </span>
              </div>
            </div>

            {request.notes && (
              <div className="info-card notes-section">
                <div className="info-card-label">{t('requestDetail.info.notes')}</div>
                <div className="info-card-value">{request.notes}</div>
              </div>
            )}
          </div>
        </div>

        <div className="request-info">
          <VatCreditAttachmentList
            requestId={request.id || request._id}
            attachments={request.attachments}
            sideBySide
          />
        </div>

        <div className="bids-section">
          <h3>
            {request.status === 'pending_allocation'
              ? t('requestDetail.bidsSection.allocationManagement')
              : request.status === 'completed'
              ? t('requestDetail.bidsSection.finalAllocation')
              : request.status === 'cancelled'
              ? t('requestDetail.bidsSection.cancelledRequest')
              : t('requestDetail.bidsSection.bidsReceived')}
          </h3>

          {bids.length > 0 ? (
            <>
              {/* Desktop Table View */}
              <div className="bids-table-container vc-bids-table-container">
                <table className={`bids-table vc-bids-table ${showAllocationColumn ? 'with-allocation' : ''} ${isCompleted ? 'with-allocated' : ''}`}>
                  <colgroup>
                    <col className="vc-col-bank" />
                    <col className="vc-col-max-amount" />
                    <col className="vc-col-rate" />
                    <col className="vc-col-commission" />
                    <col className="vc-col-fees" />
                    <col className="vc-col-maturity" />
                    <col className="vc-col-day-count" />
                    <col className="vc-col-status" />
                    {showAllocationColumn && <col className="vc-col-allocation" />}
                    {isCompleted && <col className="vc-col-allocated" />}
                    {isCompleted && <col className="vc-col-allocated-by" />}
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t('bids.bank')}</th>
                      <th>{t('vatCredit.desiredAmount')}</th>
                      <th>{t('vatCredit.cost.rate')}</th>
                      <th>{t('vatCredit.commission')}</th>
                      <th>{t('vatCredit.fees')}</th>
                      <th>{t('vatCredit.officialMaturityDateShort')}</th>
                      <th>{t('vatCredit.dayCountBasisShort')}</th>
                      <th>{t('bids.status')}</th>
                      {showAllocationColumn && <th className="vc-allocation-cell">{t('allocation.yourAllocation')}</th>}
                      {isCompleted && <th>{t('bids.allocatedAmount')}</th>}
                      {isCompleted && <th>{t('requests.allocatedBy')}</th>}                    </tr>
                  </thead>
                  <tbody>
                    {bids.map(bid => {
                      const bidId = getBidId(bid);

                      if (!bid || !bidId) {
                        console.error('Invalid bid object in render');
                        return null;
                      }

                      const isCancelled = bid.status === 'cancelled';

                      return (
                        <React.Fragment key={bidId}>
                          <tr className={isCancelled ? 'cancelled-bid' : ''}>
                            <td>
                              <div className="bank-name">
                                {bid.bank?.name || t('bids.notAvailable')}
                              </div>
                              <div className="bid-submitted-at">
                                {t('bids.submittedAt', { date: formatSubmittedAt(bid.createdAt) })}
                              </div>
                            </td>
                            <td>
                              <div className="amount-display primary">
                                {formatCurrencyWithSymbol(bid.desiredAmount, request.currency || 'EUR')}
                              </div>
                            </td>
                            <td>
                              <div className="interest-rate">
                                {formatInterestRate(bid.interestRate, bid.rateType, bid.baseRate)}
                              </div>
                            </td>
                            <td>
                              {bid.commission !== undefined && bid.commission !== null
                                ? `${bid.commission}%`
                                : t('bids.notAvailable')}
                            </td>
                            <td>
                              {bid.fees !== undefined && bid.fees !== null
                                ? formatCurrencyWithSymbol(bid.fees, request.currency || 'EUR')
                                : t('bids.notAvailable')}
                            </td>
                            <td>{formatOptionalDate(bid.officialMaturityDate, t)}</td>
                            <td>{formatDayCountBasis(bid, t) || t('bids.notAvailable')}</td>
                            <td>
                              <span className={`status status-${bid.status}`}>
                                {t(`status.${bid.status}`)}
                              </span>
                            </td>
                            {showAllocationColumn && allocations[bidId] && (
                              <td className="vc-allocation-cell">
                                <div className="allocation-input-container">
                                  <input
                                    type="text"
                                    className={`allocation-input ${isCancelled ? 'disabled' : ''}`}
                                    value={editingInputId === bidId
                                      ? editingInputValue
                                      : formatCurrencyWithSymbol(allocations[bidId].amount, request.currency || 'EUR')}
                                    onChange={(e) => !isCancelled && handleAllocationChange(bidId, e.target.value)}
                                    onFocus={() => !isCancelled && handleInputFocus(bidId)}
                                    onBlur={handleInputBlur}
                                    disabled={processingAllocation || isCancelled}
                                    placeholder={t('allocation.enterAmount')}
                                  />
                                  {isCancelled && (
                                    <div className="cancelled-bid-message">
                                      {t('requestDetail.messages.cancelledBidMessage')}
                                    </div>
                                  )}
                                </div>
                              </td>
                            )}
                            {isCompleted && (
                              <td>
                                <div className={`amount-display primary ${bid.status === 'approved' ? 'allocated' : ''}`}>
                                  {bid.status === 'approved' ? formatCurrencyWithSymbol(bid.allocatedAmount, request.currency || 'EUR') : '—'}
                                </div>
                              </td>
                            )}
                            {isCompleted && (
                              <td className="vc-allocated-by-cell">
                                {bid.status === 'approved' && bid.allocatedBy ? `${bid.allocatedBy.firstName} ${bid.allocatedBy.lastName}` : '—'}
                              </td>
                            )}
                          </tr>

                          {/* Additional details row */}
                          {bid.notes && (
                            <tr className="bid-details-row">
                              <td colSpan={8 + (showAllocationColumn ? 1 : 0) + (isCompleted ? 2 : 0)}>
                                <div className="bid-details-content">
                                  <div className="details-grid">
                                    <div className="detail-item-modern" style={{ gridColumn: '1 / -1' }}>
                                      <div className="detail-label">{t('requestDetail.bidDetails.bankNotes')}</div>
                                      <div className="detail-value">{bid.notes}</div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards View */}
              <div className="mobile-cards-container">
                {bids.map(bid => renderMobileBidCard(bid))}
              </div>

              {/* Calcolatore: costo di ogni offerta (sull'importo allocato in fase di allocazione), con formula e note */}
              {calculatorRows.length > 0 && (
                <div className="vc-calculator">
                  <div className="vc-cost-title">{t('vatCredit.calculator.title')}</div>
                  <div className="vc-calculator-description">
                    {showAllocationColumn
                      ? t('vatCredit.calculator.allocationDescription', {
                          amount: formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')
                        })
                      : isCompleted
                      ? t('vatCredit.calculator.completedDescription')
                      : t('vatCredit.calculator.description')}
                  </div>
                  <VatCreditCostBreakdown
                    rows={calculatorRows}
                    currency={request.currency || 'EUR'}
                    baseRateDate={calculatorBaseRateDate}
                    amountLabel={showAllocationColumn || isCompleted ? undefined : t('vatCredit.maxAmount')}
                    showTotals={showAllocationColumn || isCompleted}
                  />
                  {showAllocationColumn && allocationError && (
                    <div className="allocation-error">{allocationError}</div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="alert-info">
              <p>{t('requestDetail.bidsSection.noBidsYet')}</p>
            </div>
          )}
        </div>


        {request.status === 'completed' && (
          <div className="alert-success">
            <p>{t('requestDetail.messages.requestCompletedSuccess')}</p>
          </div>
        )}

        {request.status === 'cancelled' && (
          <div className="alert-danger">
            <p>{t('requestDetail.messages.requestCancelledInfo')}</p>
          </div>
        )}

        <div className="request-actions">
          {request.status === 'bidding' && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => updateRequestStatus('pending_allocation')}
              disabled={processingAction || !hasActiveBids(bids)}
              title={!hasActiveBids(bids) ? t('requestDetail.actions.atLeastOneBidRequired') : ''}
            >
              {processingAction ? t('actions.processing') : t('actions.closeBiddingProceed')}
            </button>
          )}

          {request.status === 'pending_allocation' && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={confirmAllocation}
              disabled={!!allocationError || processingAllocation || processingAction}
            >
              {processingAllocation ? t('allocation.processing') : t('allocation.confirmAllocation')}
            </button>
          )}

          {['bidding', 'pending_allocation'].includes(request.status) && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => updateRequestStatus('cancelled')}
              disabled={processingAction || processingAllocation}
            >
              {processingAction ? t('actions.processing') : t('actions.cancelRequest')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VatCreditRequestDetail;
